import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Instagram, KeyRound, Link2, RefreshCw, Unplug } from "lucide-react";

import API from "../api";

export default function InstagramConnect() {
  const [status, setStatus] = useState(null);
  const [tokenInput, setTokenInput] = useState("");

  const load = () =>
    fetch(`${API}/api/admin/instagram/status`, { credentials: "include" })
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus({ app_configured: false, connected: false }));

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    try {
      const r = await fetch(`${API}/api/instagram/authorize`, { credentials: "include" });
      const data = await r.json();
      if (data.configured && data.authorization_url) {
        window.location.assign(data.authorization_url);
      } else {
        toast.error(data.message || "Instagram is not configured yet");
      }
    } catch {
      toast.error("Could not start the connection");
    }
  };

  const disconnect = async () => {
    await fetch(`${API}/api/admin/instagram/disconnect`, { method: "POST", credentials: "include" });
    toast.success("Instagram disconnected");
    load();
  };

  const connectToken = async (e) => {
    e.preventDefault();
    const token = tokenInput.trim();
    if (!token) {
      toast.error("Paste your access token first");
      return;
    }
    try {
      const r = await fetch(`${API}/api/admin/instagram/token`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: token }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "That token could not be used");
        return;
      }
      setTokenInput("");
      toast.success(`Instagram connected${data.username ? ` as @${data.username}` : ""}`);
      load();
    } catch {
      toast.error("Could not connect with that token");
    }
  };

  const expiresIn = status && status.expires_at ? Math.ceil((new Date(status.expires_at) - new Date()) / 86400000) : null;

  return (
    <div className="mt-8" data-testid="instagram-connect">
      <h2 className="font-display text-3xl text-ivory">Instagram connection</h2>
      <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-ivory/70">
        Connect your Instagram Professional account once and the homepage &ldquo;Life on the Island&rdquo; section will
        show your latest posts and Reels automatically. We never ask for or store your Instagram password — you
        approve access on Instagram&rsquo;s own page.
      </p>

      {status && (
        <div className="mt-6 border border-ivory/15 bg-navy-2 p-6" data-testid="instagram-status">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Instagram className="h-6 w-6 text-gold" />
              <div>
                <p className="text-base text-ivory" data-testid="instagram-status-line">
                  {status.connected ? (
                    status.mode === "manual" ? (
                      <>
                        Connected via access token{status.username ? ` as @${status.username}` : ""}
                        {expiresIn !== null && expiresIn !== undefined ? (
                          <span className="text-ivory/60"> — renews itself; about {expiresIn} days remain</span>
                        ) : (
                          <span className="text-ivory/60"> — refreshed automatically every day</span>
                        )}
                      </>
                    ) : (
                      <>
                        Connected{status.username ? ` as @${status.username}` : ""}
                        {expiresIn !== null && expiresIn !== undefined && (
                          <span className="text-ivory/60"> — token renews itself; about {expiresIn} days remain</span>
                        )}
                      </>
                    )
                  ) : status.app_configured ? (
                    "Ready to connect"
                  ) : (
                    "Not set up yet"
                  )}
                </p>
                {!status.app_configured && !status.connected && (
                  <p className="mt-1 text-xs font-light text-ivory/60">
                    First, add your Meta app id and secret below — then connect.
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={connect}
                data-testid="instagram-connect-button"
                className="bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2"
              >
                {status.connected ? (
                  <span className="flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Reconnect</span>
                ) : (
                  <span className="flex items-center gap-2"><Link2 className="h-4 w-4" /> Connect Instagram</span>
                )}
              </button>
              {status.connected && (
                <button
                  onClick={disconnect}
                  data-testid="instagram-disconnect-button"
                  className="flex items-center gap-2 border border-ivory/25 px-5 py-3 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-red-400 hover:text-red-400"
                >
                  <Unplug className="h-4 w-4" /> Disconnect
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 border border-ivory/15 bg-navy-2 p-6" data-testid="instagram-token-connect">
        <h3 className="font-display text-xl text-ivory">Or connect with an access token</h3>
        <p className="mt-2 text-sm font-light leading-relaxed text-ivory/70">
          The fastest route: generate a token in Meta&rsquo;s Graph API Explorer with{" "}
          <strong className="text-ivory">instagram_basic</strong> and{" "}
          <strong className="text-ivory">pages_show_list</strong> permissions, then paste it below. The site finds
          your Collanmore Island Lodge page automatically and keeps the token renewed while your Meta app id and
          secret are saved.
        </p>
        <form onSubmit={connectToken} className="mt-4 flex flex-col gap-3 md:flex-row" data-testid="instagram-token-form">
          <input
            type="password"
            autoComplete="off"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Paste your access token"
            data-testid="instagram-token-input"
            className="w-full border border-ivory/25 bg-navy px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold focus:outline-none"
          />
          <button
            type="submit"
            data-testid="instagram-token-submit"
            className="flex items-center justify-center gap-2 border border-gold px-7 py-3 text-xs font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy"
          >
            <KeyRound className="h-4 w-4" /> Connect with token
          </button>
        </form>
      </div>

      <div className="mt-8 border border-ivory/15 bg-navy-2 p-6 md:p-8" data-testid="instagram-guide">
        <h3 className="font-display text-2xl text-ivory">One-time setup (about 10 minutes)</h3>
        <ol className="mt-5 flex flex-col gap-4 text-sm font-light leading-relaxed text-ivory/80">
          <li>
            <span className="mr-2 text-gold">1.</span>
            In the Instagram app, switch your account to <strong className="text-ivory">Professional — Business or Creator</strong>{" "}
            (Settings → Account type). This is free and instant.
          </li>
          <li>
            <span className="mr-2 text-gold">2.</span>
            Go to <strong className="text-ivory">developers.facebook.com/apps</strong> → Create App → type &ldquo;Business&rdquo;.
          </li>
          <li>
            <span className="mr-2 text-gold">3.</span>
            In the app dashboard, add the <strong className="text-ivory">Instagram</strong> product (&ldquo;API setup with Instagram
            login&rdquo;) and connect your account as a tester.
          </li>
          <li>
            <span className="mr-2 text-gold">4.</span>
            Copy the <strong className="text-ivory">Meta App ID</strong> and <strong className="text-ivory">App secret</strong>{" "}
            (App settings → Basic) and add them to the website secrets — or send them in the chat and they will be
            added for you.
          </li>
          <li>
            <span className="mr-2 text-gold">5.</span>
            In the Instagram product settings, add this website&rsquo;s callback address as a valid redirect URI:
            <code className="mt-2 block break-all bg-navy p-3 text-xs text-gold-2" data-testid="instagram-callback-url">
              {API}/api/instagram/callback
            </code>
          </li>
          <li>
            <span className="mr-2 text-gold">6.</span>
            Come back here and press <strong className="text-ivory">Connect Instagram</strong> — you&rsquo;ll approve access on
            Instagram&rsquo;s own page and return automatically. The connection then renews itself daily.
          </li>
        </ol>
        <p className="mt-5 flex items-start gap-2 text-xs font-light text-ivory/60">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          If the connection ever expires, this page will show it and the Reconnect button fixes it in one tap. The
          homepage quietly keeps showing your saved photographs until the connection is restored.
        </p>
      </div>
    </div>
  );
}
